import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { House } from "../../hooks/useHouse";

const formatHouseCode = (raw: string): string =>
  raw.replace(/\s+/g, "").toUpperCase();

const formatHouseNameFromCode = (code: string): string => {
  const c = formatHouseCode(code);
  if (!c) return "";
  return `${c.charAt(0)}${c.slice(1).toLowerCase()} House`;
};

interface HouseFormModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (formData: House) => void;
  isSubmitting?: boolean;
  isEdit?: boolean;
  initialData?: House;
}

const HouseFormModal: React.FC<HouseFormModalProps> = ({
  open,
  handleClose,
  onSubmit,
  isSubmitting = false,
  isEdit = false,
  initialData,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<House>({
    houseName: "",
    houseCode: "",
    houseColor: "#3092e3",
  });

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        const code = formatHouseCode(initialData.houseCode || "");
        setFormData({
          id: initialData.id,
          houseCode: code,
          houseName: initialData.houseName || formatHouseNameFromCode(code),
          houseColor: initialData.houseColor || "#3092e3",
        });
      } else {
        setFormData({
          houseName: "",
          houseCode: "",
          houseColor: "#3092e3",
        });
      }
    }
  }, [open, isEdit, initialData]);

  const handleHouseCodeChange = (value: string) => {
    const code = formatHouseCode(value);
    setFormData((prev) => ({
      ...prev,
      houseCode: code,
      houseName: formatHouseNameFromCode(code),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = formatHouseCode(formData.houseCode || "");
    onSubmit({
      ...(isEdit && formData.id != null ? { id: formData.id } : {}),
      houseCode: code,
      houseName: formatHouseNameFromCode(code),
      houseColor: formData.houseColor?.trim(),
    });
  };

  const isValid =
    !!formatHouseCode(formData.houseCode || "") &&
    !!formData.houseColor?.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f8f9fb",
          borderBottom: "2px solid #e0e7ff",
        }}
      >
        {isEdit ? t("edit_house") || "Edit House" : t("add_house") || "Add House"}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ mt: 1 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={t("house_code") || "House Code"}
              value={formData.houseCode || ""}
              onChange={(e) => handleHouseCodeChange(e.target.value)}
              required
              fullWidth
              placeholder={t("enter_house_code_placeholder") || "e.g. blue"}
              inputProps={{ style: { textTransform: "uppercase" } }}
            />
            <TextField
              label={t("house_name") || "House Name"}
              value={formData.houseName || ""}
              fullWidth
              disabled
              InputProps={{ readOnly: true }}
              helperText={t("house_name_auto_generated") || "Auto-generated from house code"}
            />
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <TextField
                label={t("house_color") || "House Color"}
                type="color"
                value={formData.houseColor || "#3092e3"}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, houseColor: e.target.value }))
                }
                sx={{ width: 200 }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                value={formData.houseColor || ""}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, houseColor: e.target.value }))
                }
                placeholder="#3092e3"
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button onClick={handleClose} color="inherit" disabled={isSubmitting}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isSubmitting}
            sx={{
              backgroundColor: "#3092e3",
              "&:hover": { backgroundColor: "primary.main" },
            }}
          >
            {isSubmitting
              ? t("saving") || "Saving..."
              : isEdit
                ? t("update") || "Update"
                : t("add") || "Add"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default HouseFormModal;
